import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal } from "lucide-react";
import { CarCard } from "../components/CarCard";
import { useT } from "../i18n";
import { api } from "../lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Fleet() {
  const { t } = useT();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "all",
    transmission: "all",
    fuel: "all",
    seats: "all",
  });
  const [sort, setSort] = useState("sort_pop");

  const pickup = searchParams.get("pickup");
  const ret = searchParams.get("return");

  const query = useMemo(() => {
    const p = {};
    if (filters.category !== "all") p.category = filters.category;
    if (filters.transmission !== "all") p.transmission = filters.transmission;
    if (filters.fuel !== "all") p.fuel = filters.fuel;
    if (filters.seats !== "all") p.seats = filters.seats;
    if (sort === "sort_price_asc") p.sort = "price_asc";
    if (sort === "sort_price_desc") p.sort = "price_desc";
    if (sort === "sort_new") p.sort = "newest";
    if (pickup && ret) { p.pickup_date = pickup; p.return_date = ret; }
    return p;
  }, [filters, sort, pickup, ret]);

  const { data: cars = [], isLoading } = useQuery({
    queryKey: ["cars", query],
    queryFn: async () => (await api.get("/cars", { params: query })).data,
  });

  const selectCls = "rounded-md";

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{t("fleet.title")}</h1>
        <p className="text-muted-foreground mt-2">{t("fleet.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-1">
          <div className="border border-border rounded-lg p-5 sticky top-20">
            <div className="flex items-center gap-2 mb-5">
              <SlidersHorizontal className="h-4 w-4" />
              <h2 className="font-heading font-semibold">{t("fleet.filters")}</h2>
            </div>
            <div className="space-y-5">
              {[
                { key: "category", label: t("fleet.category"), options: [["all", t("fleet.all")], ["economy", "Economy"], ["compact", "Compact"], ["suv", "SUV"], ["premium", "Premium"], ["van", "Van"], ["electric", t("fleet.electric")]] },
                { key: "transmission", label: t("fleet.transmission"), options: [["all", t("fleet.all")], ["manual", t("fleet.manual")], ["automatic", t("fleet.automatic")]] },
                { key: "fuel", label: t("fleet.fuel"), options: [["all", t("fleet.all")], ["petrol", t("fleet.petrol")], ["diesel", t("fleet.diesel")], ["electric", t("fleet.electric")], ["hybrid", t("fleet.hybrid")]] },
                { key: "seats", label: t("fleet.seats"), options: [["all", t("fleet.all")], ["2", "2+"], ["4", "4+"], ["5", "5+"], ["7", "7+"]] },
              ].map((f) => (
                <div key={f.key}>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">{f.label}</label>
                  <Select value={filters[f.key]} onValueChange={(v) => setFilters({ ...filters, [f.key]: v })}>
                    <SelectTrigger className={selectCls} data-testid={`filter-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-muted-foreground" data-testid="results-count">{cars.length} {t("fleet.results")}</p>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-[200px] rounded-md" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sort_pop">{t("fleet.sort_pop")}</SelectItem>
                <SelectItem value="sort_price_asc">{t("fleet.sort_price_asc")}</SelectItem>
                <SelectItem value="sort_price_desc">{t("fleet.sort_price_desc")}</SelectItem>
                <SelectItem value="sort_new">{t("fleet.sort_new")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <p className="text-muted-foreground py-20 text-center">{t("common.loading")}</p>
          ) : cars.length === 0 ? (
            <p className="text-muted-foreground py-20 text-center" data-testid="no-cars">{t("fleet.no_cars")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {cars.map((car, i) => <CarCard key={car.car_id} car={car} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
